import { useEffect, useState } from "react";
import styled from "styled-components";
import { AddItemsPickerButton } from "../../../../shared/orderLineForm/components/addItemsPickerStyles";
import { FulfilLineGrid } from "../FulfilLineGrid";
import {
  SectionHint,
  SectionTitle,
  SelectionPill,
  StepFooter,
  StepOptions,
  StepResult,
  StepState,
} from "../FulfilParts";
import { trimQty } from "../fulfilHelpers";
import { useFulfilStep } from "../useFulfilStep";
import { useStepSelection } from "../useStepSelection";

const COLUMNS = [
  { key: "pack", label: "Pack", render: row => row.name || `#${row.stock_pack_id}` },
  { key: "warehouse", label: "From", render: row => row.warehouse?.name || "—" },
  { key: "lines", label: "Lines", numeric: true, render: row => row.line_count },
  { key: "qty", label: "Units", numeric: true, render: row => trimQty(row.total_qty) },
];

const isAmount = value => value === "" || Number.isFinite(Number(value));

const InvalidNote = styled.p`
  margin: 12px 0 0;
  color: #ad4e00;
  font-size: 13px;
`;

/**
 * Step 4: put ready packs on a delivery, and the delivery on a shipment.
 *
 * @param {{orderId: (string|number), onNext: function()}} props
 */
export const ShipStep = ({ orderId, onNext }) => {
  const { query, rows, defaults, run, saving, result, clearResult } = useFulfilStep(
    orderId,
    "ship",
  );
  const selection = useStepSelection(rows, { idKey: "stock_pack_id" });
  const [dispatchDate, setDispatchDate] = useState("");
  const [customerValue, setCustomerValue] = useState("");
  const [insuranceValue, setInsuranceValue] = useState("0");
  const [createShipment, setCreateShipment] = useState(true);

  useEffect(() => {
    setDispatchDate(defaults.planned_dispatch_date || "");
    setCustomerValue(trimQty(defaults.customer_value ?? ""));
    setInsuranceValue(trimQty(defaults.insurance_value ?? "0"));
    if (defaults.create_shipment !== undefined)
      setCreateShipment(Boolean(defaults.create_shipment));
  }, [defaults]);

  if (result) {
    return (
      <StepResult result={result} nextLabel="Next: Invoice" onNext={onNext} onAgain={clearResult} />
    );
  }

  const count = selection.selectedRows.length;
  const valuesValid = isAmount(customerValue) && isAmount(insuranceValue);

  const submit = () =>
    run({
      stock_pack_ids: selection.selectedRows.map(row => row.stock_pack_id),
      ...(dispatchDate ? { planned_dispatch_date: dispatchDate } : {}),
      customer_value: customerValue || "0",
      insurance_value: insuranceValue || "0",
      create_shipment: createShipment,
      ...(createShipment && defaults.currency_id ? { currency_id: defaults.currency_id } : {}),
    });

  return (
    <>
      <SectionTitle>Ship</SectionTitle>
      <SectionHint>
        Packs that are ready for dispatch go on a delivery. Tick "Also create a shipment" to book
        the delivery onto a shipment in the same step.
      </SectionHint>
      <StepState query={query} emptyText="No packs are ready for dispatch yet.">
        <FulfilLineGrid
          rows={rows}
          idKey="stock_pack_id"
          columns={COLUMNS}
          selection={selection}
          disabled={saving}
          ariaLabel="Packs to ship"
        />
        <StepOptions>
          <label>
            Planned dispatch
            <input
              type="date"
              value={dispatchDate}
              onChange={event => setDispatchDate(event.target.value)}
            />
          </label>
          <label>
            Customer value
            <input
              type="text"
              inputMode="decimal"
              value={customerValue}
              onChange={event => setCustomerValue(event.target.value)}
            />
          </label>
          <label>
            Insurance value
            <input
              type="text"
              inputMode="decimal"
              value={insuranceValue}
              onChange={event => setInsuranceValue(event.target.value)}
            />
          </label>
          <label className="inline">
            <input
              type="checkbox"
              checked={createShipment}
              onChange={event => setCreateShipment(event.target.checked)}
            />
            Also create a shipment
          </label>
        </StepOptions>
        {valuesValid ? null : (
          <InvalidNote role="status">Customer and insurance values must be numbers.</InvalidNote>
        )}
        <StepFooter pill={<SelectionPill selected={count} total={rows.length} kind="items" />}>
          <AddItemsPickerButton
            type="primary"
            loading={saving}
            disabled={!count || !valuesValid || saving}
            onClick={submit}
          >
            {createShipment ? "Create delivery and shipment" : "Create delivery"}
          </AddItemsPickerButton>
        </StepFooter>
      </StepState>
    </>
  );
};
